import { Box, Button, TextField, Typography } from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';

import { Stack } from '$layouts/v2/components/Stack.jsx';
import { TemplateInput } from '$layouts/v2/components/TemplateInput.jsx';
import type { FieldInfo } from '$layouts/v2/helpers/context.js';

/** Maps a referenced sub-workflow's declared inputs from the parent context. */
export function SubWorkflowInputs({
  inputs,
  mapping,
  availableFields,
  onChange,
  readOnly = false,
}: {
  inputs: v4.FieldSpec[] | undefined;
  mapping: Record<string, string>;
  availableFields: FieldInfo[];
  onChange: (mapping: Record<string, string>) => void;
  readOnly?: boolean;
}) {
  if (inputs === undefined) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        Loading sub-workflow inputs…
      </Typography>
    );
  }
  if (inputs.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        This sub-workflow declares no inputs. Open it and use “Inputs” to
        declare what it needs.
      </Typography>
    );
  }
  return (
    <Box>
      <Typography variant="subtitle2">Input mapping</Typography>
      <Typography
        variant="body2"
        gutterBottom
        sx={{
          color: 'text.secondary',
        }}
      >
        The sub-workflow runs isolated — it only sees what you map here.
      </Typography>
      <Stack spacing={1.5}>
        {inputs.map((input) => {
          const value = mapping[input.name] ?? '';
          const missing = !input.optional && !value.trim();
          return (
            <Box key={input.name}>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography component="code" sx={{ fontFamily: 'monospace' }}>
                  {input.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {input.inputType}
                  {!input.optional && ' · required'}
                </Typography>
              </Stack>
              <TemplateInput
                value={value}
                fields={availableFields}
                onChange={(nextValue) =>
                  onChange({ ...mapping, [input.name]: nextValue })
                }
                multiline={false}
                readOnly={readOnly}
              />
              {missing && (
                <Typography variant="caption" color="error">
                  Required
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

/** Maps a referenced sub-workflow's declared outputs back into the parent context. */
export function SubWorkflowOutputs({
  outputs,
  mapping,
  availableFields,
  onChange,
  readOnly = false,
}: {
  outputs: v4.FieldSpec[] | undefined;
  mapping: Record<string, string>;
  availableFields: FieldInfo[];
  onChange: (mapping: Record<string, string>) => void;
  readOnly?: boolean;
}) {
  if (outputs === undefined || outputs.length === 0) {
    return null;
  }
  const existing = new Set(availableFields.map((field) => field.name));
  const mapAll = () => {
    const next = { ...mapping };
    for (const output of outputs) {
      if (!(output.name in next)) {
        next[output.name] = output.name;
      }
    }
    onChange(next);
  };
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">Output mapping</Typography>
        {!readOnly && (
          <Button size="small" onClick={mapAll}>
            Map all (same name)
          </Button>
        )}
      </Stack>
      <Typography
        variant="body2"
        gutterBottom
        sx={{
          color: 'text.secondary',
        }}
      >
        Store the sub-workflow&apos;s results in your context — available from
        this step onwards. Leave blank to discard.
      </Typography>
      <Stack spacing={1.5}>
        {outputs.map((output) => {
          const target = mapping[output.name] ?? '';
          const overwrites =
            target.trim() !== '' && existing.has(target.trim());
          return (
            <Box key={output.name}>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography component="code" sx={{ fontFamily: 'monospace' }}>
                  {output.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {output.inputType}
                </Typography>
              </Stack>
              <TextField
                placeholder="parent field name (blank = discard)"
                value={target}
                onChange={(event) =>
                  onChange({ ...mapping, [output.name]: event.target.value })
                }
                error={overwrites}
                slotProps={{ htmlInput: { readOnly } }}
                helperText={
                  overwrites
                    ? `Overwrites existing “${target.trim()}”.`
                    : undefined
                }
              />
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
